// ** React Imports
import { useState, useContext, useCallback } from 'react'

// ** Next Imports
import Link from 'next/link'

import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Checkbox from '@mui/material/Checkbox'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import { styled } from '@mui/material/styles'
import FormHelperText from '@mui/material/FormHelperText'
import InputAdornment from '@mui/material/InputAdornment'
import Typography from '@mui/material/Typography'
import MuiFormControlLabel from '@mui/material/FormControlLabel'

// ** Icon Imports
import Icon from 'src/@core/components/icon'

import { useForm, Controller } from 'react-hook-form'
import BlankLayout from 'src/@core/layouts/BlankLayout'

// ** Demo Imports
// import Logo from `/images/TotientXCP_Logo.jpg`
// import Logo from './images/Logo.png'
// import { AuthContext } from 'src/context/AuthContext'
import LoginClientList from 'src/views/login/loginClientList'
import { useAuth } from 'src/hooks/useAuth'
import { AuthContext } from 'src/context/AuthContext'
import LoginFooter from 'src/views/login/LoginFooter'
import InlineSaveStatus from 'src/@core/components/InlineSaveStatus'
// ** Styled Components
const RightWrapper = styled(Box)(({ _theme }) => ({
  width: '100%',
  maxWidth: 360
}))

const BoxWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.down('md')]: {
    maxWidth: 400
  }
}))

const FormControlLabel = styled(MuiFormControlLabel)(({ theme }) => ({
  '& .MuiFormControlLabel-label': {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary
  }
}))

const defaultValues = {
  password: '',
  email: ''
}

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false)

  const [shrinkPassword, setShrinkPassword] = useState(false)

  const [shrinkEmail, setShrinkEmail] = useState(false)

  const [openClientList, setOpenClientList] = useState(false)
  const [loginLoading, setLoginLoading] = useState(false)

  const {
    getLoginClientList,
    clientData,
    selectedClientId,
    setSelectedClientId,
    handleLoginClient,
    tempAuthToken,
    loginEmail,
    loginPassword,
    setLoginEmail,
    setLoginPassword,
    rememberMe,
    setRememberMe,
    loginError,
    setLoginError
  } = useContext(AuthContext)

  const { logoUrlFromSettings } = useAuth(AuthContext)

  // ** Hooks

  const {
    control,
    setError,
    handleSubmit,
    clearErrors,
    formState: { errors }
  } = useForm({
    defaultValues,
    mode: 'onBlur'
  })


  const capitalizeFirstLetter = string => {
    return string.charAt(0).toUpperCase() + string.slice(1)
  }

  const handlecloseClientList = () => {
    setOpenClientList(false)
  }

  const handleopenClientList = () => {
    setOpenClientList(true)
  }

  const authenticate = () => {
    handleSubmit(onSubmit)()
  }

 const onSubmit = async () => {
  clearErrors()

  let hasError = false

  const emailFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  if (!loginEmail?.trim()) {
    setError('email', {
      type: 'manual',
      message: 'Email is required'
    })
    hasError = true
  } else if (!emailFormat.test(loginEmail.trim())) {
    setError('email', {
      type: 'manual',
      message: 'Email format is invalid'
    })
    hasError = true
  }

  if (!loginPassword?.trim()) {
    setError('password', {
      type: 'manual',
      message: 'Password is required'
    })
    hasError = true
  } else if (loginPassword.length > 250) {
    setError('password', {
      type: 'manual',
      message: 'Invalid Password field values'
    })
    hasError = true
  }

  const passwordCriteria =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/

  if (loginPassword && !passwordCriteria.test(loginPassword)) {
    setError('password', {
      type: 'manual',
      message:
        'Password must be at least 8 characters long, include an uppercase letter, a lowercase letter, a number, and a special character.'
    })
    hasError = true
  }

  if (hasError) return

  setLoginLoading(true)

  try {
    await getLoginClientList(handleopenClientList)
  } finally {
    setLoginLoading(false)
  }
}

  const handleAutofillAnimation = useCallback(event => {
    if (event.animationName === 'mui-auto-fill' || event.animationName === 'onAutoFillStart') {
      const target = event.target
      if (target.id === 'auth-login-v2-email') setShrinkEmail(true)
      if (target.id === 'auth-login-v2-password') setShrinkPassword(true)
    }
  }, [])

  const passwordCriteria =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/

const passwordFormatError =
  loginPassword &&
  !passwordCriteria.test(loginPassword)

  return (
    <Box className='content-right' sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          backgroundImage: 'url(/images/login_bg.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          position: 'relative',
          pr: 8,
          py: 8
        }}
      >
        <RightWrapper sx={{
          position: 'relative',
          zIndex: 1,
          borderRadius: '20px',
          border: '4px solid #38bdf8',
          boxShadow: '0 6px 24px rgba(0,0,0,0.8), 0 12px 40px rgba(0,0,0,0.6)',
          overflow: 'hidden'
        }}>
          <Box
            sx={{
              px: 8,
              pt: 10,
              pb: 10,
              minHeight: 560,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              backgroundColor: 'background.paper'
            }}
          >
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
              <img
                src={logoUrlFromSettings ? logoUrlFromSettings : '/images/cxpify_PNG.png'}
                alt='TotientCXP Logo'
                height={90}
              />
            </Box>
            <BoxWrapper>

              <form noValidate autoComplete='off'>
                <FormControl
                  fullWidth
                  sx={{
                    mb: 4,
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px #fff inset',
                      WebkitTextFillColor: 'rgba(76, 78, 100, 0.87)'
                    }
                  }}
                >
                  <Controller
                    name='email'
                    control={control}
                    render={({ field: { value, onChange, onBlur } }) => (
                      <TextField
                        autoFocus
                        id='auth-login-v2-email'
                        label='Email'
                        // onFocus={setShrinkEmail(true)}
                        autoComplete='username-new'
                        value={value}
                        onAnimationStart={handleAutofillAnimation}
                        onBlur={onBlur}
                        onChange={e => {
                          const inputValue = e.target.value
                          onChange(inputValue)
                          setLoginEmail(inputValue)
                          // setShrinkEmail(true)
                          setShrinkEmail(inputValue.trim() !== '')
                          if (inputValue.trim() === '') {
                            setError('email', { type: 'manual', message: 'Email is required' })
                          } else {
                            clearErrors('email')
                          }
                          setLoginError('')
                        }}
                        error={Boolean(errors.email)}
                        placeholder='admin@gmail.com'
                        inputProps={{
                          autoComplete: 'off'
                        }}
                        InputLabelProps={{
                          shrink: shrinkEmail
                        }}
                      />
                    )}
                  />
                  {errors.email && (
                    <FormHelperText sx={{ color: 'error.main' }}>
                      {capitalizeFirstLetter(errors.email.message)}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl
                  fullWidth
                  sx={{
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px #fff inset',
                      WebkitTextFillColor: 'rgba(76, 78, 100, 0.87)'
                    }
                  }}
                >
                  <Controller
                    name='password'
                    control={control}
                    render={({ field: { value, onChange, onBlur } }) => (
                      <TextField
                       value={value || ''}
    onBlur={onBlur}
                        onAnimationStart={handleAutofillAnimation}
                        onChange={e => {
  const inputValue = e.target.value

  onChange(inputValue)
  setLoginPassword(inputValue)
  setShrinkPassword(inputValue.trim() !== '')
  setLoginError('')

  if (!inputValue.trim()) {
    setError('password', {
      type: 'manual',
      message: 'Password is required'
    })
  } else {
    clearErrors('password')
  }
}}
                        label='Password'
                        id='auth-login-v2-password'
                       error={Boolean(errors.password) || Boolean(passwordFormatError)}
                        type={showPassword ? 'text' : 'password'}
                        variant='outlined'
                        fullWidth
                        //autoComplete='current-password'
                        placeholder='Password'
                        inputProps={{
                          maxLength: 250
                        }}
                        InputLabelProps={{
                          shrink: shrinkPassword
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>
                              <IconButton
                                edge='end'
                                data-testid='icon-button-pswd'
                                onMouseDown={e => e.preventDefault()}
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                <Icon icon={showPassword ? 'mdi:eye-outline' : 'mdi:eye-off-outline'} fontSize={20} />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    )}
                  />
                  {(errors.password || passwordFormatError) && (
  <FormHelperText sx={{ color: 'error.main' }}>
    {errors.password?.message ||
      'Password must be at least 8 characters long, include an uppercase letter, a lowercase letter, a number, and a special character.'}
  </FormHelperText>
)}
                </FormControl>

                <Box
                  sx={{
                    mb: 4,
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between'
                  }}
                >
                  <FormControlLabel
                    label='Remember Me'
                    control={<Checkbox checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} />}
                  />
                  <Typography
                    variant='body2'
                    component={Link}
                    href='/forgot-password'
                    sx={{ color: 'primary.main', textDecoration: 'none' }}
                  >
                    Forgot Password?
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  size='large'
                  data-testid='login-button'
                  // type='submit'
                  onClick={authenticate}
                  variant='contained'
                  startIcon={loginLoading ? <CircularProgress size={18} color='inherit' /> : null}
                  sx={{ pointerEvents: loginLoading ? 'none' : 'auto' }}
                >
                  {loginLoading ? 'Logging in...' : 'Login'}
                </Button>
                {loginError && (
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <InlineSaveStatus type='error' message={loginError} onClose={() => setLoginError('')} />
                  </Box>
                )}
              </form>
            </BoxWrapper>
          </Box>

      </RightWrapper>
      <LoginClientList
        openClientList={openClientList}
        handlecloseClientList={handlecloseClientList}
        clientData={clientData}
        selectedClientId={selectedClientId}
        setSelectedClientId={setSelectedClientId}
        handleLoginClient={handleLoginClient}
        tempAuthToken={tempAuthToken}
      />
      </Box>
      <LoginFooter />
    </Box>
  )
}

LoginPage.getLayout = page => <BlankLayout>{page}</BlankLayout>
LoginPage.guestGuard = true

export default LoginPage
